/**
 * Itinerary Submissions Page
 * Standalone page for submitting and managing coordination itineraries
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import ItinerarySubmissions from '../components/ItinerarySubmissions';
import ItineraryMapGL, {
  DeviceTrack,
  colorForDeviceType,
  normalizeItineraryDeviceType,
} from '../components/ItineraryMapGL';
import StudentAdminForm from '../components/StudentAdminForm';
import SubmissionsList from '../components/SubmissionsList';
import ObservationsForm from '@signals/app-shell/components/Observations/ObservationsForm';
import ObservationsList from '@signals/app-shell/components/Observations/ObservationsList';
import DeviceStatusNotification from '../components/DeviceStatusNotification';
import { ItinerarySubmission, RouteDataEntry } from '../services/itineraryApi';
import { useItinerarySubmissionStore } from '../stores/itinerarySubmissionStore';
import * as observationsApi from '@signals/app-shell/services/observationsApi';
import * as pathfinderApi from '@signals/pathfinder-ui/services/pathfinderApi';
import { useAuth } from '@signals/app-shell/hooks/useAuth';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`itinerary-tabpanel-${index}`}
      aria-labelledby={`itinerary-tab-${index}`}
      style={{ height: index === 2 || index === 4 ? '100%' : 'auto' }}
      {...other}
    >
      <Box sx={{ height: index === 2 || index === 4 ? '100%' : 'auto', display: value === index ? 'block' : 'none' }}>
        {children}
      </Box>
    </div>
  );
}

// Scoped compact theme for the entire Itinerary / Observations tab surface.
// Merges into the outer theme (preserves palette / dark mode) and sets uniform
// small-size defaults plus tighter spacing so all four tabs share one look.
const buildCompactTheme = (outer: Theme): Theme =>
  createTheme(outer, {
    typography: {
      fontSize: 13,
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8rem' },
      subtitle1: { fontSize: '0.9rem' },
      subtitle2: { fontSize: '0.8rem' },
      caption: { fontSize: '0.7rem' },
      button: { fontSize: '0.8rem', textTransform: 'none' as const },
      h5: { fontSize: '1.1rem', fontWeight: 600 },
      h6: { fontSize: '0.95rem', fontWeight: 600 },
    },
    components: {
      MuiTextField: { defaultProps: { size: 'small', margin: 'dense' } },
      MuiFormControl: { defaultProps: { size: 'small', margin: 'dense' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiInputBase: { defaultProps: { margin: 'dense' } },
      MuiButton: {
        defaultProps: { size: 'small' },
        styleOverrides: { root: { minHeight: 30, paddingTop: 4, paddingBottom: 4 } },
      },
      MuiIconButton: { defaultProps: { size: 'small' } },
      MuiChip: { defaultProps: { size: 'small' } },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 12,
            '&:last-child': { paddingBottom: 12 },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: { minHeight: 40, '&.Mui-expanded': { minHeight: 40 } },
          content: {
            margin: '6px 0',
            '&.Mui-expanded': { margin: '6px 0' },
          },
        },
      },
      MuiAccordionDetails: { styleOverrides: { root: { padding: 12 } } },
      MuiListItem: { defaultProps: { dense: true } },
      MuiDialogTitle: { styleOverrides: { root: { padding: '12px 16px', fontSize: '1rem' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '8px 16px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '8px 12px' } } },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            fontSize: '0.8rem',
            padding: '6px 12px',
            textTransform: 'none',
          },
        },
      },
    },
  });

const ItineraryPage: React.FC = () => {
  const activeTabStorageKey = 'coordination-itinerary-active-tab';
  const loadItinerarySubmission = useItinerarySubmissionStore((state) => state.loadSubmission);
  const { user } = useAuth();
  const { singular } = useParticipantLabels();
  const theme = useTheme();
  const compactTheme = React.useMemo(() => buildCompactTheme(theme), [theme]);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  
  // Check if user is a student or trainee (by role or username pattern)
  const isStudent = user?.roles?.includes('student');
  const usernameLower = user?.username?.toLowerCase() || '';
  const isTrainee = usernameLower.includes('trainee');
  const isStudentInUsername = usernameLower.includes('student');
  const isStudentOrTrainee = isStudent || isTrainee || isStudentInUsername;
  
  const [activeTab, setActiveTab] = useState(() => {
    const storedTab = window.sessionStorage.getItem(activeTabStorageKey);
    const parsedTab = storedTab !== null ? Number(storedTab) : 0;
    return Number.isInteger(parsedTab) && parsedTab >= 0 && parsedTab <= 4 ? parsedTab : 0;
  });
  const [mobilePanelSize, setMobilePanelSize] = useState<'minimized' | 'half' | 'expanded'>('half');
  const [showDeviceNotification, setShowDeviceNotification] = useState(true);
  
  // Map interaction state
  const [isLocationSelectionMode, setIsLocationSelectionMode] = useState(false);
  const [selectedEntryIndex, setSelectedEntryIndex] = useState<number>(-1);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lon: number; address?: string; entryIndex: number } | null>(null);
  const [routeEntries, setRouteEntries] = useState<RouteDataEntry[]>([]);
  
  // Observations state
  const [vehicleObservations, setVehicleObservations] = useState<observationsApi.VehicleObservation[]>([]);
  const [individualObservations, setIndividualObservations] = useState<observationsApi.IndividualObservation[]>([]);
  const [_observationMapMode, setObservationMapMode] = useState<'vehicle' | 'individual' | null>(null);
  const [_selectedObservationIndex, setSelectedObservationIndex] = useState<number>(-1);
  const [editingObservation, setEditingObservation] = useState<observationsApi.Observation | null>(null);
  
  // Student route data state (for observations map)
  const [deviceTracks, setDeviceTracks] = useState<DeviceTrack[]>([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeDataLoaded, setRouteDataLoaded] = useState(false);
  
  // Resizable panel state
  const [mapWidth, setMapWidth] = useState(65); // Percentage - map larger by default
  const [isDragging, setIsDragging] = useState(false);
  
  // Map reference for triggering resize
  const mapRef = useRef<any>(null);
  
  // Trigger map resize when device notification panel is toggled
  useEffect(() => {
    // Small delay to allow DOM to update
    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    }, 350); // Match the transition duration
    return () => clearTimeout(timer);
  }, [showDeviceNotification]);
  
  // Cycle through panel sizes on mobile
  const cycleMobilePanelSize = () => {
    setMobilePanelSize(prev => {
      if (prev === 'minimized') return 'half';
      if (prev === 'half') return 'expanded';
      return 'minimized';
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    window.sessionStorage.setItem(activeTabStorageKey, String(newValue));
  };

  const switchTab = useCallback((tabIndex: number) => {
    setActiveTab(tabIndex);
    window.sessionStorage.setItem(activeTabStorageKey, String(tabIndex));
  }, []);

  // Handle map click for location selection
  const handleMapClick = useCallback((lat: number, lon: number, address?: string) => {
    if (isLocationSelectionMode && selectedEntryIndex !== -1) {
      setSelectedLocation({ lat, lon, address, entryIndex: selectedEntryIndex });
      setIsLocationSelectionMode(false);
      setSelectedEntryIndex(-1);
    }
  }, [isLocationSelectionMode, selectedEntryIndex]);

  // Handle entering location selection mode
  const handleEnterLocationSelection = useCallback((entryIndex: number) => {
    // Clear prior pick so shared state cannot re-apply stale coords when map mode toggles
    setSelectedLocation(null);
    setIsLocationSelectionMode(true);
    setSelectedEntryIndex(entryIndex);
  }, []);

  // Handle route entries update from form
  const handleRouteEntriesUpdate = (entries: RouteDataEntry[]) => {
    setRouteEntries(entries);
  };
  
  // Handle observations map mode (stable refs so ObservationsForm location effect is not spammed)
  const handleEnterObservationMapMode = useCallback((_type: 'vehicle' | 'individual', index: number) => {
    // ObservationsForm's effect depends on map mode; stale selectedLocation would re-apply the last click
    setSelectedLocation(null);
    setObservationMapMode(_type);
    setSelectedObservationIndex(index);
    setIsLocationSelectionMode(true);
    setSelectedEntryIndex(index);
  }, []);

  const handleExitObservationMapMode = useCallback(() => {
    setObservationMapMode(null);
    setSelectedObservationIndex(-1);
    setIsLocationSelectionMode(false);
    setSelectedEntryIndex(-1);
    setSelectedLocation(null);
  }, []);
  
  // Handle observations change from form
  const handleObservationsChange = useCallback((
    vehicles: observationsApi.VehicleObservation[],
    individuals: observationsApi.IndividualObservation[]
  ) => {
    setVehicleObservations(vehicles);
    setIndividualObservations(individuals);
  }, []);
  
  // Handle edit observation
  const handleEditObservation = (observation: observationsApi.Observation) => {
    setEditingObservation(observation);
    // Switch to Submit Observations tab to allow editing
    switchTab(3);
  };
  
  // Handle edit observation complete
  const handleEditObservationComplete = () => {
    setEditingObservation(null);
    // Switch back to View Observations tab
    switchTab(4);
  };

  const handleReopenItinerarySubmission = useCallback((submission: ItinerarySubmission) => {
    loadItinerarySubmission(submission);
    setRouteEntries(submission.route_entries || []);
    switchTab(1);
  }, [loadItinerarySubmission, switchTab]);

  // Load student GPS tracks for observations (per-device lines; no dwells)
  const handleLoadRouteData = useCallback(async (startDate: Date, endDate: Date, studentNumber: string) => {
    if (!studentNumber) {
      console.warn('No student number provided for route loading');
      return;
    }
    
    console.log(`Loading route data for ${studentNumber} from ${startDate.toISOString()} to ${endDate.toISOString()}`);
    setRouteLoading(true);
    
    try {
      // Query GPS points for the student using the direct postgres endpoint
      console.log('📡 Querying GPS points...');
      const points = await pathfinderApi.default.queryDataPoints({
        dateRange: [startDate, endDate],
        studentIds: [studentNumber],
        deviceTypes: ['phone', 'watch', 'vehicle'],
        limit: 5000
      });
      
      console.log(`Received ${points.length} GPS points`);

      // Keep each device as its own track (do not merge phone/watch/vehicle).
      // Group by normalized type + device id so phone/vehicle never collapse
      // into one line when device ids collide or types are inconsistently cased.
      const byDevice = new Map<string, DeviceTrack>();
      for (const p of points) {
        const lat = Number(p.coordinates?.[0]);
        const lon = Number(p.coordinates?.[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

        const rawType = p.deviceType || 'unknown';
        const deviceType = normalizeItineraryDeviceType(rawType);
        const deviceId =
          p.deviceId ||
          `${p.studentId || studentNumber}-${deviceType}`;
        const groupKey = `${deviceType}::${deviceId}`;

        let track = byDevice.get(groupKey);
        if (!track) {
          track = {
            deviceId,
            deviceType,
            color: colorForDeviceType(deviceType),
            points: [],
          };
          byDevice.set(groupKey, track);
        }
        track.points.push({
          latitude: lat,
          longitude: lon,
          timestamp: p.timestamp instanceof Date ? p.timestamp : new Date(p.timestamp),
        });
      }

      const tracks: DeviceTrack[] = Array.from(byDevice.values())
        .map((track) => ({
          ...track,
          points: track.points.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
        }))
        .filter((t) => t.points.length > 0);

      console.log(
        `Built ${tracks.length} device track(s):`,
        tracks.map((t) => `${t.deviceType}/${t.deviceId}:${t.points.length}:${t.color}`).join(', ')
      );
      setDeviceTracks(tracks);

      // Dwells are intentionally not loaded for observation "Load My Route"
      // tracks — only per-device GPS lines are shown.
      setRouteDataLoaded(tracks.length > 0);

      if (tracks.length === 0) {
        console.warn('⚠️ No route data found for the selected time range');
      }
    } catch (error: any) {
      console.error('Failed to load route data:', error);
      console.error('Error details:', error.response?.data || error.message);
      setRouteDataLoaded(false);
    } finally {
      setRouteLoading(false);
    }
  }, []);

  // Clear route data
  const handleClearRouteData = useCallback(() => {
    setDeviceTracks([]);
    setRouteDataLoaded(false);
  }, []);

  const handleSelectedLocationConsumed = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  // Handle resize dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const container = document.getElementById('resizable-container');
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    
    // Limit between 20% and 80%
    if (newWidth >= 20 && newWidth <= 80) {
      setMapWidth(newWidth);
    }
  }, [isDragging]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const tabs = [
    {
      label: `${singular} Admin Data`,
      component: <StudentAdminForm />
    },
    {
      label: 'Submit Itinerary',
      component: (
        <ItinerarySubmissions
          selectedLocation={selectedLocation}
          onEnterMapMode={handleEnterLocationSelection}
          onRouteEntriesChange={handleRouteEntriesUpdate}
          onSelectedLocationConsumed={handleSelectedLocationConsumed}
        />
      )
    },
    {
      label: 'View Itineraries',
      component: (
        <SubmissionsList
          mapWidth={mapWidth}
          onResizeStart={handleMouseDown}
          isDragging={isDragging}
          isActive={activeTab === 2}
          onReopenSubmission={handleReopenItinerarySubmission}
        />
      )
    },
    {
      label: 'Submit Observations',
      component: (
        <ObservationsForm
          vehicleObservations={vehicleObservations}
          individualObservations={individualObservations}
          selectedLocation={selectedLocation}
          onEnterMapMode={handleEnterObservationMapMode}
          onExitMapMode={handleExitObservationMapMode}
          onObservationsChange={handleObservationsChange}
          editData={editingObservation || undefined}
          onEditComplete={handleEditObservationComplete}
          onLoadRouteData={handleLoadRouteData}
          routeLoading={routeLoading}
          routeDataLoaded={routeDataLoaded}
          onClearRouteData={handleClearRouteData}
        />
      )
    },
    {
      label: 'View Observations',
      component: <ObservationsList onEditObservation={handleEditObservation} mapWidth={mapWidth} onResizeStart={handleMouseDown} isDragging={isDragging} isActive={activeTab === 4} />
    },
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden' 
    }}>
      {/* Page Header */}
      <Box sx={{ 
        bgcolor: 'background.paper', 
        color: 'text.primary', 
        p: { xs: 1, md: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.75 } }}>
          <Box sx={{ width: 4, alignSelf: 'stretch', minHeight: { xs: 30, md: 40 }, borderRadius: 1, bgcolor: 'secondary.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.5rem' } }}>
              Coordination Itinerary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Submit and manage surveillance reports
            </Typography>
          </Box>
        </Box>
      </Box>

      <ThemeProvider theme={compactTheme}>
        {/* Content Area - Responsive Layout */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tabs */}
          <Box sx={{ 
            borderBottom: 2, 
            borderColor: 'divider', 
            flexShrink: 0, 
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{
                minHeight: { xs: 44, md: 52 },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
                '& .MuiTab-root': {
                  minHeight: { xs: 44, md: 52 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                  fontWeight: 500,
                  textTransform: 'none',
                  px: { xs: 1.5, sm: 2.5 },
                  minWidth: { xs: 'auto', md: 140 },
                  letterSpacing: '0.02em',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Device Status Notification - Only show for students/trainees */}
          {user && isStudentOrTrainee && showDeviceNotification && (
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <DeviceStatusNotification onClose={() => setShowDeviceNotification(false)} />
            </Box>
          )}

          {/* Main Content - Responsive Layout */}
          <Box 
            id="resizable-container"
            sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Map - For Submit Itinerary and Submit Observations tabs */}
            {(activeTab === 1 || activeTab === 3) && (
              <>
                <Box sx={{ 
                  width: { xs: '100%', md: `${mapWidth}%` },
                  flex: {
                    xs: mobilePanelSize === 'minimized' ? '1 1 auto' :
                        mobilePanelSize === 'half' ? '0 0 50%' : '0 0 25%',
                    md: 'none'
                  },
                  minHeight: {
                    xs: mobilePanelSize === 'minimized' ? 'calc(100% - 48px)' :
                        mobilePanelSize === 'half' ? '45vh' : '20vh',
                    md: '100%'
                  },
                  bgcolor: 'background.default',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'all 0.3s ease',
                }}>
                  <ItineraryMapGL
                    routeEntries={activeTab === 1 ? routeEntries : []}
                    onMapClick={handleMapClick}
                    isLocationSelectionMode={isLocationSelectionMode}
                    selectedEntryIndex={selectedEntryIndex}
                    vehicleObservations={activeTab === 3 ? vehicleObservations : []}
                    individualObservations={activeTab === 3 ? individualObservations : []}
                    deviceTracks={activeTab === 3 ? deviceTracks : []}
                    onMapReady={(map) => { mapRef.current = map; }}
                  />
                </Box>
                
                {/* Resizable Divider - Desktop only */}
                <Box
                  onMouseDown={handleMouseDown}
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    width: '4px',
                    bgcolor: isDragging ? 'primary.main' : 'divider',
                    cursor: 'col-resize',
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                    transition: 'background-color 0.2s',
                  }}
                />
              </>
            )}

            {/* Form Content - With mobile panel controls */}
            <Box sx={{ 
              flex: {
                xs: (activeTab === 1 || activeTab === 3)
                  ? (mobilePanelSize === 'minimized' ? '0 0 48px' :
                     mobilePanelSize === 'half' ? '0 0 50%' : '0 0 75%')
                  : 1,
                md: 1
              },
              overflow: activeTab === 2 || activeTab === 4 ? 'hidden' : 'auto',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
            }}>
              {/* Mobile Panel Toggle - Only for map tabs on mobile */}
              {isMobile && (activeTab === 1 || activeTab === 3) && (
                <Box
                  onClick={cycleMobilePanelSize}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 0.5,
                    borderBottom: mobilePanelSize !== 'minimized' ? 1 : 0,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&::before': {
                      content: '""',
                      width: 40,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'grey.400',
                      position: 'absolute',
                      top: 6,
                    },
                    position: 'relative',
                    minHeight: 24,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {mobilePanelSize === 'minimized' ? <KeyboardArrowUpIcon fontSize="small" /> :
                     mobilePanelSize === 'half' ? <KeyboardArrowUpIcon fontSize="small" /> : 
                     <KeyboardArrowDownIcon fontSize="small" />}
                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                      {mobilePanelSize === 'minimized' ? 'Tap to expand form' :
                       mobilePanelSize === 'half' ? 'Tap for full form' : 'Tap to show map'}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Panel Content - Show when not minimized on mobile */}
              {((isMobile && (activeTab === 1 || activeTab === 3) && mobilePanelSize !== 'minimized') ||
                !isMobile || (activeTab !== 1 && activeTab !== 3)) && (
                <Box sx={{ flex: 1, overflow: activeTab === 2 || activeTab === 4 ? 'hidden' : 'auto', overflowX: 'hidden' }}>
                  {tabs.map((tab, index) => (
                    <TabPanel key={index} value={activeTab} index={index}>
                      {tab.component}
                    </TabPanel>
                  ))}
                </Box>
              )}
              
              {/* Minimized state indicator on mobile */}
              {isMobile && (activeTab === 1 || activeTab === 3) && mobilePanelSize === 'minimized' && (
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}>
                  Tap above to show form
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    </Box>
  );
};

export default ItineraryPage;
